import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useRolePerms } from '../features/auth/useRolePerms.js';
import { AddContactModal } from '../components/domain/AddContactModal.js';
import { UploadModal } from '../components/domain/UploadModal.js';
import { downloadCSV } from '../lib/csv.js';
import { useResetSub } from '../features/filters/useFilters.js';
import { SearchableSelect } from '../components/primitives/SearchableSelect.js';

interface Contact {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  contactType: 'Customer' | 'Worker' | 'Supplier' | 'Contractor' | 'Consultant' | 'Internal' | 'Other' | null;
  customerId: string | null;
  customer: string | null;
  role: string | null;
  dept: string | null;
  linkedin: string | null;
  projects: string[];
}

interface Project {
  id: string;
  code: string;
  name: string;
  status: string;
  customerId?: string | null;
  customerRef?: { id: string; name: string } | null;
}

interface CustomerLite { id: string; name: string }

type SortCol = 'name' | 'contactType' | 'customer' | 'dept' | 'role' | 'email' | 'phone' | null;
type SortDir = 1 | -1;

function initials(name: string): string {
  return name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();
}

const AVC = ['#E8A427', '#22C55E', '#3B82F6', '#8B5CF6', '#EF4444', '#06B6D4', '#F97316', '#10B981', '#EC4899', '#14B8A6'];
function avc(name: string): string {
  return AVC[name.charCodeAt(0) % AVC.length] ?? AVC[0]!;
}

function avEl(name: string, size = 24, font = 9): JSX.Element {
  return (
    <div className="av" style={{ width: size, height: size, fontSize: font, background: avc(name) }}>
      {initials(name)}
    </div>
  );
}

function LiSvg() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" style={{ verticalAlign: 'middle' }}>
      <path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19a.66.66 0 000 .14V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z"/>
    </svg>
  );
}

function linkedinLink(c: Contact): { href: string; direct: boolean } {
  if (c.linkedin) {
    return { href: c.linkedin.startsWith('http') ? c.linkedin : `https://www.linkedin.com/in/${c.linkedin}`, direct: true };
  }
  const q = encodeURIComponent(`${c.name} ${c.customer ?? ''}`.trim());
  return { href: `https://www.linkedin.com/search/results/people/?keywords=${q}`, direct: false };
}

const ctTypeCols: Record<string, [string, string]> = {
  Customer: ['rgba(232,164,39,.15)', 'var(--accent)'],
  Worker: ['rgba(34,197,94,.15)', '#22c55e'],
  Supplier: ['rgba(59,130,246,.15)', 'var(--info)'],
  Contractor: ['rgba(168,85,247,.15)', '#a855f7'],
  Consultant: ['rgba(236,72,153,.15)', '#ec4899'],
  Internal: ['rgba(156,163,175,.15)', '#9ca3af'],
  Other: ['rgba(107,114,128,.15)', '#6b7280'],
};

function ThHdr({ label, col, current, dir, onClick }: { label: string; col: Exclude<SortCol, null>; current: SortCol; dir: SortDir; onClick: (c: Exclude<SortCol, null>) => void }) {
  const arrow = current === col ? (dir === 1 ? ' ▲' : ' ▼') : '';
  return <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => onClick(col)}>{label}{arrow}</th>;
}

interface SharedDocLite { id: string; name: string; createdAt: string; recipients: Array<{ contactId?: string | null; supplierContactId?: string | null; status: string; signedAt: string | null; declineReason?: string | null }> }

export function Contacts() {
  const { data: contactDocs = [] } = useQuery({ queryKey: ['shared-docs'], queryFn: () => api.get<SharedDocLite[]>('/shared-docs').then((r) => r.data) });
  const navigate = useNavigate();
  const { canImport, canExport, canEditContacts } = useRolePerms();
  const [search, setSearch] = useState('');
  const [fType, setFType] = useState('');
  const [fCompany, setFCompany] = useState('');
  const [fDept, setFDept] = useState('');
  const [fRole, setFRole] = useState('');
  const [sortCol, setSortCol] = useState<SortCol>(null);
  const [sortDir, setSortDir] = useState<SortDir>(1);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState<typeof contacts[number] | null>(null);

  useResetSub(() => { setSearch(''); setFType(''); setFDept(''); setFRole(''); });

  const { data: contacts = [] } = useQuery({
    queryKey: ['contacts'],
    queryFn: () => api.get<Contact[]>('/contacts').then((r) => r.data),
  });
  const { data: projects = [] } = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.get<Project[]>('/projects').then((r) => r.data),
  });
  const { data: customersList = [] } = useQuery({
    queryKey: ['customers'],
    queryFn: () => api.get<CustomerLite[]>('/customers').then((r) => r.data),
  });

  const companyOptions = useMemo(() => [...new Set(contacts.map((c) => c.customer).filter(Boolean) as string[])].sort(), [contacts]);
  const deptOptions = useMemo(() => [...new Set(contacts.map((c) => c.dept).filter(Boolean) as string[])].sort(), [contacts]);
  const roleOptions = useMemo(() => [...new Set(contacts.map((c) => c.role).filter(Boolean) as string[])].sort(), [contacts]);

  const rows = useMemo(() => {
    let r = [...contacts];
    const s = search.toLowerCase().trim();
    if (s) r = r.filter((c) => [c.name, c.customer, c.role, c.dept, c.email, c.phone, c.contactType].filter(Boolean).join(' ').toLowerCase().includes(s));
    if (fType) r = r.filter((c) => (c.contactType ?? 'Other') === fType);
    if (fCompany) r = r.filter((c) => c.customer === fCompany);
    if (fDept) r = r.filter((c) => c.dept === fDept);
    if (fRole) r = r.filter((c) => c.role === fRole);
    if (sortCol) {
      r = [...r].sort((a, b) => {
        const get = (c: Contact): string => ((c[sortCol] as string | null) ?? '');
        return get(a).localeCompare(get(b)) * sortDir;
      });
    }
    return r;
  }, [contacts, search, fType, fCompany, fDept, fRole, sortCol, sortDir]);

  const sub = `${contacts.length} contacts`;
  const countLabel = `${rows.length} contact${rows.length !== 1 ? 's' : ''} shown`;

  const active = activeId ? contacts.find((c) => c.id === activeId) : null;
  const activeProjects = active ? projects.filter((p) => active.projects.includes(p.code) || active.projects.includes(p.id)) : [];

  const handleSort = (col: Exclude<SortCol, null>) => {
    if (sortCol === col) setSortDir((d) => (d === 1 ? -1 : 1));
    else { setSortCol(col); setSortDir(1); }
  };

  const clearFilters = () => { setSearch(''); setFType(''); setFCompany(''); setFDept(''); setFRole(''); };

  return (
    <div className="page" id="page-contacts">
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, overflowY: 'auto', maxHeight: 'calc(100vh - 110px)' }}>
          <div className="hbar">
            <div>
              <div className="ph">Contacts</div>
              <div className="ps" id="cont-sub">{sub}</div>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {canImport ? <button className="btn btn-ghost btn-sm" onClick={() => setImportOpen(true)}>⬆ Import</button> : null}
              {canExport ? <button className="btn btn-ghost btn-sm" onClick={() => downloadCSV('contacts.csv', rows as any, [
                { key: 'name', label: 'Name' },
                { key: 'contactType', label: 'Type' },
                { key: 'role', label: 'Role' },
                { key: 'dept', label: 'Department' },
                { key: 'customer', label: 'Company' },
                { key: 'email', label: 'Email' },
                { key: 'phone', label: 'Phone' },
              ])}>⬇ Export</button> : null}
              {canEditContacts ? <button className="btn btn-primary" onClick={() => setAddOpen(true)}>+ Add Contact</button> : null}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap', marginBottom: 10, padding: '5px 8px', background: 'var(--card)', borderRadius: 8, border: '1px solid var(--border)', minHeight: 36 }}>
            <div className="tbl-search" style={{ minWidth: 180, flex: 1, margin: 0 }}>
              <span style={{ color: 'var(--faint)' }}>⌕</span>
              <input id="cont-search" placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <div className="snav-sep"></div>
            <div className="fpill">
              <span>Type:</span>
              <SearchableSelect
                id="cont-f-type"
                value={fType}
                onChange={setFType}
                placeholder="All"
                options={[
                  { value: '', label: 'All' },
                  { value: 'Customer', label: 'Customer' },
                  { value: 'Worker', label: 'Worker' },
                  { value: 'Supplier', label: 'Supplier' },
                  { value: 'Contractor', label: 'Contractor' },
                  { value: 'Consultant', label: 'Consultant' },
                  { value: 'Internal', label: 'Internal' },
                  { value: 'Other', label: 'Other' },
                ]}
              />
            </div>
            <div className="fpill">
              <span>Company:</span>
              <SearchableSelect
                id="cont-f-company"
                value={fCompany}
                onChange={setFCompany}
                placeholder="All"
                options={[{ value: '', label: 'All' }, ...companyOptions.map((c) => ({ value: c, label: c }))]}
              />
            </div>
            <div className="fpill">
              <span>Dept:</span>
              <SearchableSelect
                id="cont-f-dept"
                value={fDept}
                onChange={setFDept}
                placeholder="All"
                options={[{ value: '', label: 'All' }, ...deptOptions.map((d) => ({ value: d, label: d }))]}
              />
            </div>
            <div className="fpill">
              <span>Role:</span>
              <SearchableSelect
                id="cont-f-role"
                value={fRole}
                onChange={setFRole}
                placeholder="All"
                options={[{ value: '', label: 'All' }, ...roleOptions.map((r) => ({ value: r, label: r }))]}
              />
            </div>
            <div className="snav-sep"></div>
            <span id="cont-count" style={{ fontSize: 10, color: 'var(--muted)' }}>{countLabel}</span>
            <button className="btn btn-ghost btn-xs" onClick={clearFilters} title="Clear filters" style={{ marginLeft: 'auto' }}>✕ Clear</button>
          </div>
          <div className="card">
            <table>
              <thead id="cont-thead">
                <tr>
                  <ThHdr label="Name" col="name" current={sortCol} dir={sortDir} onClick={handleSort} />
                  <ThHdr label="Type" col="contactType" current={sortCol} dir={sortDir} onClick={handleSort} />
                  <ThHdr label="Company" col="customer" current={sortCol} dir={sortDir} onClick={handleSort} />
                  <ThHdr label="Dept" col="dept" current={sortCol} dir={sortDir} onClick={handleSort} />
                  <ThHdr label="Role" col="role" current={sortCol} dir={sortDir} onClick={handleSort} />
                  <ThHdr label="Email" col="email" current={sortCol} dir={sortDir} onClick={handleSort} />
                  <ThHdr label="Phone" col="phone" current={sortCol} dir={sortDir} onClick={handleSort} />
                  <th>Projects</th>
                </tr>
              </thead>
              <tbody id="cont-tbody">
                {rows.map((ct) => {
                  const typeKey = ct.contactType ?? 'Other';
                  const cols = ctTypeCols[typeKey] ?? ctTypeCols.Other!;
                  const li = linkedinLink(ct);
                  return (
                    <tr key={ct.id} onClick={() => setActiveId(ct.id)} style={{ cursor: 'pointer' }}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          {avEl(ct.name, 24, 9)}
                          <span style={{ fontWeight: 500 }}>{ct.name}</span>
                          <a
                            href={li.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            title={li.direct ? 'Open LinkedIn profile' : 'Search LinkedIn'}
                            style={{ color: li.direct ? '#0A66C2' : 'var(--faint)', display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}
                          ><LiSvg /></a>
                        </div>
                      </td>
                      <td>
                        <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 5, fontWeight: 600, background: cols[0], color: cols[1] }}>
                          {ct.contactType ?? '—'}
                        </span>
                      </td>
                      <td style={{ color: 'var(--muted)' }}>{ct.customer ?? ''}</td>
                      <td>{ct.dept ? <span className="pill">{ct.dept}</span> : null}</td>
                      <td>{ct.role ? <span className="pill">{ct.role}</span> : null}</td>
                      <td style={{ color: 'var(--info)', fontSize: 11 }}>{ct.email ?? ''}</td>
                      <td style={{ color: 'var(--muted)' }}>{ct.phone ?? ''}</td>
                      <td>
                        {ct.projects.map((code) => (
                          <span key={code} className="pill" style={{ margin: 1 }}>{code}</span>
                        ))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <div className="dp" id="cont-dp" style={{ display: active ? 'block' : 'none', maxHeight: 'calc(100vh - 90px)' }}>
          {active ? (() => {
            const li = linkedinLink(active);
            const cust = active.customerId
              ? customersList.find((c) => c.id === active.customerId)
              : (active.customer ? customersList.find((c) => c.name === active.customer) : null);
            return (
              <>
                {/* Top row: empty-div | Edit center | × right (per prototype line 6001) */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div></div>
                  {canEditContacts ? <button className="btn btn-primary btn-sm" onClick={() => setEditing(active)}>✏ Edit</button> : <div></div>}
                  <button className="close-btn" onClick={() => setActiveId(null)}>×</button>
                </div>

                {/* Avatar (block-level, left-aligned) + centered text stack — matches prototype */}
                <div style={{ textAlign: 'center', marginBottom: 13 }}>
                  {avEl(active.name, 48, 16)}
                  <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 14, marginTop: 8 }}>{active.name}</div>
                  {active.role ? <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{active.role}</div> : null}
                  {active.contactType ? (
                    <span style={{ fontSize: 10, padding: '2px 9px', borderRadius: 5, fontWeight: 600, marginTop: 4, display: 'inline-block', background: (ctTypeCols[active.contactType] ?? ctTypeCols.Other!)[0], color: (ctTypeCols[active.contactType] ?? ctTypeCols.Other!)[1] }}>
                      {active.contactType}
                    </span>
                  ) : null}
                  {active.customer ? (
                    <div
                      onClick={() => cust && navigate(`/customers?id=${cust.id}`)}
                      style={{ fontSize: 12, color: 'var(--accent)', cursor: cust ? 'pointer' : 'default', marginTop: 3, fontWeight: 600 }}
                    >
                      {active.customer}{cust ? ' ↗' : ''}
                    </div>
                  ) : null}
                  <div style={{ fontSize: 10, color: 'var(--faint)', marginTop: 2 }}>
                    {activeProjects.length} project{activeProjects.length !== 1 ? 's' : ''} linked
                  </div>
                </div>

                <div className="divider"></div>

                {(() => {
                  const rows = contactDocs.flatMap((d) => d.recipients.filter((r) => r.contactId === active.id).map((r) => ({ id: d.id, name: d.name, createdAt: d.createdAt, status: r.status, signedAt: r.signedAt, declineReason: r.declineReason ?? null })));
                  return (
                    <div id="contact-docs" style={{ marginBottom: 10 }}>
                      <div className="sec-title">Documents ({rows.length})</div>
                      {rows.length === 0 ? <div style={{ fontSize: 11, color: 'var(--muted)' }}>Nothing shared for signature yet</div> : rows.map((d) => (
                        <div key={d.id} onClick={() => navigate('/docsign')} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, fontSize: 11, padding: '6px 0', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</div>
                            <div style={{ fontSize: 10, color: 'var(--muted)' }}>Shared {new Date(d.createdAt).toLocaleDateString('en-GB')}{d.signedAt && d.status === 'signed' ? ` · signed ${new Date(d.signedAt).toLocaleDateString('en-GB')}` : ''}{d.declineReason ? ` · "${d.declineReason}"` : ''}</div>
                          </div>
                          {d.status === 'signed' ? <span className="sign-chip-signed">✓ SIGNED</span> : d.status === 'declined' ? <span className="sign-chip-pending">✕ DECLINED</span> : <span className="sign-chip-pending">⏱ PENDING</span>}
                        </div>
                      ))}
                    </div>
                  );
                })()}

                <div className="dp-row">
                  <span className="dp-key">Email</span>
                  <span className="dp-val">
                    {active.email ? <a href={`mailto:${active.email}`} style={{ color: 'var(--info)', fontSize: 12 }}>{active.email}</a> : <span style={{ fontSize: 12, color: 'var(--faint)' }}>—</span>}
                  </span>
                </div>
                <div className="dp-row">
                  <span className="dp-key">Phone</span>
                  <span className="dp-val">
                    {active.phone ? <a href={`tel:${active.phone}`} style={{ color: 'var(--text)', textDecoration: 'none', fontSize: 12 }}>{active.phone}</a> : <span style={{ fontSize: 12, color: 'var(--faint)' }}>—</span>}
                  </span>
                </div>
                <div className="dp-row">
                  <span className="dp-key">LinkedIn</span>
                  <span className="dp-val">
                    <a
                      href={li.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: li.direct ? '#0A66C2' : 'var(--muted)', fontSize: 12, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 5 }}
                      title={li.direct ? 'Open LinkedIn profile' : 'Search LinkedIn for this person'}
                    >
                      <LiSvg /> {li.direct ? 'Open profile ↗' : 'Search LinkedIn ↗'}
                    </a>
                  </span>
                </div>
                {active.dept ? (
                  <div className="dp-row">
                    <span className="dp-key">Dept</span>
                    <span className="dp-val"><span className="pill">{active.dept}</span></span>
                  </div>
                ) : null}

                <div className="divider"></div>
                <div className="sec-title">Projects</div>
                {activeProjects.length === 0 ? (
                  <div style={{ fontSize: 11, color: 'var(--muted)' }}>No projects linked</div>
                ) : (
                  activeProjects.map((p) => (
                    <div
                      key={p.id}
                      onClick={() => navigate(`/projects?id=${p.id}`)}
                      style={{ display: 'flex', alignItems: 'center', gap: 7, padding: 8, borderRadius: 8, border: '1px solid var(--border)', marginBottom: 6, cursor: 'pointer', background: 'var(--card)', transition: 'border-color .15s' }}
                      onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'var(--accent)')}
                      onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
                    >
                      <span className={`dot dot-${p.status}`}></span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12, fontWeight: 500 }}>{p.name}</div>
                        <div style={{ fontSize: 10, color: 'var(--accent)', fontFamily: 'Syne, sans-serif', fontWeight: 700 }}>{p.code}</div>
                      </div>
                      <span className={`badge b-${p.status}`}>{p.status}</span>
                    </div>
                  ))
                )}

                <div className="divider"></div>
                {canEditContacts ? (
                  <button
                    className="btn btn-primary btn-sm"
                    style={{ width: '100%', justifyContent: 'center' }}
                    onClick={() => setEditing(active)}
                  >✏ Edit Contact</button>
                ) : null}
              </>
            );
          })() : null}
        </div>
      </div>
      {addOpen ? <AddContactModal onClose={() => setAddOpen(false)} /> : null}
      {editing ? <AddContactModal onClose={() => setEditing(null)} initial={editing as any} /> : null}
      {importOpen ? <UploadModal entity="contacts" onClose={() => setImportOpen(false)} /> : null}
    </div>
  );
}
